import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Compass } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import { useNetwork } from '@/contexts/NetworkContext'
import { SitesApi } from '@/services/sites/sitesApi'

export interface SiteSelectProps {
  /** Site id, or '' for no selection. */
  value: string
  onChange: (siteId: string) => void
  /** Offers only this publisher's sites. Omit for every site in the network. */
  earningsAccountId?: string
  /** Label for the empty choice, offered first to clear the selection. */
  anyLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Searchable site picker, optionally narrowed to one publisher. With
 * `anyLabel` it serves as a list filter, where '' means "not filtered".
 */
export function SiteSelect({ earningsAccountId, anyLabel, ...props }: SiteSelectProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()

  const { data: sites = [], isLoading } = useQuery({
    queryKey: ['sites', 'picker', scopeKey, earningsAccountId ?? ''],
    queryFn: () =>
      earningsAccountId ? SitesApi.getByEarningsAccount(earningsAccountId) : SitesApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })
  const options = useMemo(() => sites.map((s) => ({ value: s.id, label: s.name })), [sites])

  return (
    <SearchSelect
      {...props}
      options={options}
      placeholder={anyLabel ?? t('adUnits.selectSite')}
      clearable={!!anyLabel}
      icon={Compass}
      loading={isLoading}
      searchPlaceholder={t('sites.searchSites')}
      emptyText={t('sites.noSitesFound')}
    />
  )
}
